import { useAsyncData } from '#imports'
import { ofetch } from 'ofetch'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'

import type {
  CloudinaryResponse,
  IngestResult,
  UploadConfig,
  UploadSignature,
  UploadedFile,
} from '../../api/upload-service/types'
import {
  FileUploadError,
  isAlreadyIngestedError,
  toFileUploadError,
} from '../utils/FileUploadError'

type FileParams = {
  publicId: string
  resourceType: string
  fileFormat: string
}

type RequestOptions = {
  // Cancels the call. An aborted call rejects with the native `AbortError`,
  // never with `FileUploadError`, so `signal.aborted` checks keep working.
  signal?: AbortSignal
}

function createAbortError() {
  return new DOMException('The operation was aborted.', 'AbortError')
}

// ofetch wraps an aborted request in a `FetchError`; surface the native error.
function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) throw createAbortError()
}

// Settles like `promise`, or rejects with the native `AbortError` as soon as
// the signal aborts (or at once if it is already aborted). The abort listener
// is removed once settled.
async function raceAbort<T>(promise: Promise<T>, signal?: AbortSignal) {
  if (!signal) return promise
  throwIfAborted(signal)

  let rejectAborted: (reason: DOMException) => void = () => undefined
  const aborted = new Promise<never>((_resolve, reject) => {
    rejectAborted = reject
  })
  const onAbort = () => rejectAborted(createAbortError())
  signal.addEventListener('abort', onAbort, { once: true })

  try {
    return await Promise.race([promise, aborted])
  } finally {
    signal.removeEventListener('abort', onAbort)
  }
}

function getExtension(fileName: string) {
  const dotIndex = fileName.lastIndexOf('.')

  return dotIndex > 0 ? fileName.slice(dotIndex + 1).toLowerCase() : ''
}

const INGEST_RESERVED_KEYS = ['publicId', 'assetId', 'resourceType', 'format']

// Builds the ingest body: consumer `extra` fields first, then the four upload
// result fields which always win. `format` is added only when the result has
// one, so an extra `format` never survives on a raw upload.
function buildIngestBody<TExtra extends object>(
  uploaded: UploadedFile,
  extra: TExtra | undefined,
): Record<string, unknown> {
  const body: Record<string, unknown> = {}

  if (extra) {
    const fields = extra as Record<string, unknown>
    for (const key of Object.keys(fields)) {
      if (!INGEST_RESERVED_KEYS.includes(key)) body[key] = fields[key]
    }
  }

  body.publicId = uploaded.publicId
  body.assetId = uploaded.assetId
  body.resourceType = uploaded.resourceType
  if (uploaded.format !== undefined) body.format = uploaded.format

  return body
}

// Call it in a setup or composable context: it registers the shared use case
// config with `useAsyncData`.
// serviceUploadBaseUrl is the base url of the service that will be used to upload the file.
// signatureRetry (default true): on a Cloudinary 401 (rejected or expired signature),
// request a fresh signature and retry the upload once.
// maxFileSize (bytes, optional): reject a file of this size or more before any request.
// Client-side UX check only, no server-side limit exists per use case.
export function useFileUpload({
  serviceUploadBaseUrl,
  useCase,
  signatureRetry = true,
  maxFileSize,
}: {
  serviceUploadBaseUrl: string
  useCase: string
  signatureRetry?: boolean
  maxFileSize?: number
}) {
  const $httpFetch = useHttpFetch()

  // The use case config is required before any upload. Keyed by base URL and
  // use case, so every instance on the page shares one request. A failed load
  // keeps no data, so the next call requests it again.
  const config = useAsyncData(
    `nuxt-module-upload:config:${serviceUploadBaseUrl}:${useCase}`,
    () =>
      $httpFetch('/:serviceUploadBaseUrl/file-uploader/:useCase/config', {
        pathParams: {
          serviceUploadBaseUrl,
          useCase,
        },
      }),
    { dedupe: 'defer', immediate: false },
  )

  // The shared request never takes a caller's signal, since other callers may
  // await it: an aborted call stops waiting and rejects at once.
  async function getUploadConfig({
    signal,
  }: RequestOptions = {}): Promise<UploadConfig> {
    throwIfAborted(signal)

    if (!config.data.value) {
      await raceAbort(config.execute({ dedupe: 'defer' }), signal)
    }

    if (config.data.value) return config.data.value

    // useAsyncData wraps the rejection in a NuxtError whose cause is the
    // original fetch error.
    const error = config.error.value
    throw toFileUploadError(error?.cause ?? error, 'config')
  }

  async function getUploadSignature({ signal }: RequestOptions) {
    try {
      return await $httpFetch(
        '/:serviceUploadBaseUrl/file-uploader/:useCase/sign',
        {
          method: 'POST',
          pathParams: {
            serviceUploadBaseUrl,
            useCase,
          },
          signal,
        },
      )
    } catch (error) {
      throwIfAborted(signal)
      throw toFileUploadError(error, 'sign')
    }
  }

  // Read and delete routes keep their raw errors; only an abort is normalized.
  async function withAbort<T>(
    signal: AbortSignal | undefined,
    request: Promise<T>,
  ) {
    try {
      return await request
    } catch (error) {
      throwIfAborted(signal)
      throw error
    }
  }

  async function getDownloadUrl({
    publicId,
    resourceType,
    fileFormat,
    signal,
  }: FileParams & RequestOptions) {
    return withAbort(
      signal,
      $httpFetch('/:serviceUploadBaseUrl/file-uploader/:useCase/download-url', {
        method: 'GET',
        pathParams: {
          serviceUploadBaseUrl,
          useCase,
        },
        query: {
          fileFormat,
          publicId,
          resourceType,
        },
        signal,
      }),
    )
  }

  async function getStatus({
    publicId,
    resourceType,
    fileFormat,
    signal,
  }: FileParams & RequestOptions) {
    return withAbort(
      signal,
      $httpFetch('/:serviceUploadBaseUrl/file-uploader/:useCase/status', {
        method: 'GET',
        pathParams: {
          serviceUploadBaseUrl,
          useCase,
        },
        query: {
          fileFormat,
          publicId,
          resourceType,
        },
        signal,
      }),
    )
  }

  async function deleteFile({
    publicId,
    resourceType,
    fileFormat,
    signal,
  }: FileParams & RequestOptions) {
    return withAbort(
      signal,
      $httpFetch('/:serviceUploadBaseUrl/file-uploader/:useCase', {
        method: 'DELETE',
        pathParams: {
          serviceUploadBaseUrl,
          useCase,
        },
        query: {
          fileFormat,
          publicId,
          resourceType,
        },
        signal,
      }),
    )
  }

  function buildFormData({
    file,
    uploadSignature,
  }: {
    file: File
    uploadSignature: UploadSignature
  }) {
    const form = new FormData()
    const uploadFields = uploadSignature.fields

    form.append('file', file)
    Object.entries(uploadFields).forEach(([key, value]) => {
      form.append(key, String(value))
    })

    return form
  }

  async function signAndUpload(file: File, { signal }: RequestOptions) {
    // Call sign endpoint to get the signature
    const uploadSignature = await getUploadSignature({ signal })

    // Build the form data
    const form = buildFormData({
      file,
      uploadSignature,
    })

    // Upload directly to Cloudinary with `ofetch` — no internal headers,
    // no manual Content-Type (the browser sets the multipart boundary itself).
    // `ofetch` throws on HTTP error statuses and parses the JSON body for us.
    try {
      return await ofetch<CloudinaryResponse>(uploadSignature.url, {
        body: form,
        method: 'POST',
        signal,
      })
    } catch (error) {
      throwIfAborted(signal)
      throw toFileUploadError(error, 'upload')
    }
  }

  // Fast path for a v1 fallback: resolves with null when the use case kill
  // switch is on. `/config` may be cached by the browser for up to an hour,
  // so the backend `/sign` kill switch stays authoritative.
  async function getEnabledUploadConfig(
    options: RequestOptions = {},
  ): Promise<UploadConfig | null> {
    const uploadConfig = await getUploadConfig(options)

    return uploadConfig.isFileUploaderEnabled ? uploadConfig : null
  }

  function assertValidSize(file: File) {
    if (file.size === 0) {
      throw new FileUploadError({ step: 'upload', reason: 'EMPTY_FILE' })
    }
    if (maxFileSize !== undefined && file.size >= maxFileSize) {
      throw new FileUploadError({ step: 'upload', reason: 'FILE_TOO_LARGE' })
    }
  }

  // UX check only: the upload preset stays the security boundary. An empty
  // `allowedFormats` defers the check to the preset, like the SDK sign route.
  function assertAllowedFormat(file: File, { allowedFormats }: UploadConfig) {
    if (allowedFormats.length === 0) return

    const extension = getExtension(file.name)
    const isAllowed = allowedFormats.some(
      (format) => format.toLowerCase() === extension,
    )

    if (!isAllowed) {
      throw new FileUploadError({ step: 'upload', reason: 'INVALID_FILE' })
    }
  }

  async function upload(
    file: File,
    { signal }: RequestOptions = {},
  ): Promise<UploadedFile> {
    assertValidSize(file)

    const uploadConfig = await getUploadConfig({ signal })

    if (!uploadConfig.isFileUploaderEnabled) {
      throw new FileUploadError({ step: 'config', reason: 'UPLOAD_DISABLED' })
    }

    assertAllowedFormat(file, uploadConfig)

    let cloudinaryResult: CloudinaryResponse

    try {
      cloudinaryResult = await signAndUpload(file, { signal })
    } catch (error) {
      // Only a Cloudinary 401 retries; a failed sign request never does.
      const isRetryable =
        signatureRetry &&
        error instanceof FileUploadError &&
        error.step === 'upload' &&
        error.reason === 'SIGNATURE_REJECTED'

      if (!isRetryable) throw error

      cloudinaryResult = await signAndUpload(file, { signal })
    }

    return {
      assetId: cloudinaryResult.asset_id,
      format: cloudinaryResult.format,
      publicId: cloudinaryResult.public_id,
      resourceType: cloudinaryResult.resource_type,
      secureUrl: cloudinaryResult.secure_url,
    }
  }

  // Ingests an upload result into the consumer backend. The module owns the
  // four upload result fields: they always win over the same keys in `extra`,
  // and `format` is only sent when the upload result has one. `TExtra` is the
  // consumer's domain fields (a plain object type, so `type` aliases and
  // `interface` declarations both work), `TResponse` the handler response. The
  // response is cast, not validated at runtime.
  //
  // The use case config gates the call: a failed config load keeps its `config`
  // error, and a config without the `ingest` route rejects before any request.
  // A `409 /errors/already-ingested` resolves as a retryable success.
  async function ingest<
    TExtra extends object = Record<string, never>,
    TResponse = unknown,
  >(
    uploaded: UploadedFile,
    { extra, signal }: { extra?: TExtra; signal?: AbortSignal } = {},
  ): Promise<IngestResult<TResponse>> {
    const uploadConfig = await getUploadConfig({ signal })

    if (!uploadConfig.availableRoutes.includes('ingest')) {
      throw new FileUploadError({ step: 'ingest', reason: 'ROUTE_UNAVAILABLE' })
    }

    const body = buildIngestBody(uploaded, extra)

    try {
      const data = await $httpFetch(
        '/:serviceUploadBaseUrl/file-uploader/:useCase/ingest',
        {
          method: 'POST',
          pathParams: {
            serviceUploadBaseUrl,
            useCase,
          },
          body,
          signal,
        },
      )

      return { alreadyIngested: false, data: data as TResponse }
    } catch (error) {
      throwIfAborted(signal)

      if (isAlreadyIngestedError(error)) {
        return { alreadyIngested: true }
      }

      throw toFileUploadError(error, 'ingest')
    }
  }

  return {
    deleteFile,
    getDownloadUrl,
    getEnabledUploadConfig,
    getStatus,
    getUploadConfig,
    ingest,
    upload,
  }
}
